import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { ACCOUNT_SETTINGS_PAGES } from '../../routing/routeConfiguration';
import { LinkTabNavHorizontal } from '../../components';

import css from './UserNav.module.css';

/**
 * A component that renders a navigation bar for a user-specific pages.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} props.currentPage - The current page (e.g. 'ManageListingsPage')
 * @param {boolean} [props.showManageListingsLink] - Whether to show the manage listings link
 * @param {string} [props.inboxTab] - Which inbox tab to show (sales or orders)
 * @param {Object} props.currentUser - The current user object
 * @returns {JSX.Element} User navigation component
 */
const UserNav = props => {
  const { className, rootClassName, currentPage, showManageListingsLink, inboxTab = 'sales', currentUser } = props;
  const classes = classNames(rootClassName || css.root, className);

  // Check if user is super
  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  const isSuperUser = userType === 'super';
  
  // Classic listings UI is for super users only; providers use My Services
  const manageListingsTabMaybe =
    userType !== 'provider-main' && (showManageListingsLink || isSuperUser)
      ? [
          {
            text: <FormattedMessage id="UserNav.yourListings" />,
            selected: currentPage === 'ManageListingsPage',
            disabled: false,
            linkProps: {
              name: 'ManageListingsPage',
            },
          },
        ]
      : [];

  const myServicesTabMaybe =
    userType === 'provider-main'
      ? [
          {
            text: <FormattedMessage id="UserNav.myServices" />,
            selected: currentPage === 'ProviderServicesPage',
            disabled: false,
            linkProps: {
              name: 'ProviderServicesPage',
            },
          },
        ]
      : [];

  const adminTabMaybe = isSuperUser
    ? [
        {
          text: <FormattedMessage id="UserNav.admin" defaultMessage="Admin" />,
          selected: currentPage === 'AdminPage',
          disabled: false,
          linkProps: {
            name: 'AdminPage',
          },
        },
        {
          text: <FormattedMessage id="UserNav.dashboard" defaultMessage="Dashboard" />,
          selected: currentPage === 'DashboardPage',
          disabled: false,
          linkProps: {
            name: 'DashboardPage',
          },
        },
        {
          text: <FormattedMessage id="UserNav.features" />,
          selected: currentPage === 'FeaturesPage',
          disabled: false,
          linkProps: {
            name: 'FeaturesPage',
          },
        },
      ]
    : [];

  const tabs = [
    ...myServicesTabMaybe,
    ...manageListingsTabMaybe,
    ...adminTabMaybe,
    {
      text: <FormattedMessage id="UserNav.inbox" />,
      selected: currentPage === 'InboxPage',
      disabled: false,
      linkProps: {
        name: 'InboxPage',
        params: { tab: inboxTab },
      },
    },
    {
      text: <FormattedMessage id="UserNav.profileSettings" />,
      selected: currentPage === 'ProfileSettingsPage',
      disabled: false,
      linkProps: {
        name: 'ProfileSettingsPage',
      },
    },
    
    {
      text: <FormattedMessage id="UserNav.accountSettings" />,
      selected: ACCOUNT_SETTINGS_PAGES.includes(currentPage),
      disabled: false,
      linkProps: {
        name: 'ContactDetailsPage',
      },
    },
    {
      text: 'My Gift Cards',
      selected: currentPage === 'MyGiftCardPage'|| currentPage === 'TrackBalancePage',
      disabled: false,
      linkProps: {
        name: 'TrackBalancePage',
      },
    },

  ];

  return (
    <LinkTabNavHorizontal 
      className={classes} 
      tabRootClassName={css.tab} 
      tabs={tabs} 
      skin="dark" 
    />
  );
};

export default UserNav;
